import { useState, useCallback, useRef } from 'react';
import { Block } from '../types';

export const useBlockSelection = () => {
  const [selectedBlocks, setSelectedBlocks] = useState<Set<string>>(new Set());
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<{ x: number; y: number } | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<{ x: number; y: number } | null>(null);
  
  const selectionRef = useRef<HTMLDivElement>(null);

  // Sélectionner une plage de blocs (shift+click)
  const selectRange = useCallback((ids: string[]) => {
    setSelectedBlocks(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.add(id));
      return next;
    });
  }, []);

  // Sélectionner un bloc
  const selectBlock = useCallback((blockId: string, isAdditive = false) => {
    if (isAdditive) {
      setSelectedBlocks(prev => {
        const newSet = new Set(prev);
        if (newSet.has(blockId)) {
          newSet.delete(blockId);
        } else {
          newSet.add(blockId);
        }
        return newSet;
      });
    } else {
      setSelectedBlocks(new Set([blockId]));
    }
  }, []);

  // Toggle la sélection d'un bloc (pour les checkboxes)
  const toggleSelection = useCallback((blockId: string) => {
    setSelectedBlocks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(blockId)) {
        newSet.delete(blockId);
      } else {
        newSet.add(blockId);
      }
      return newSet;
    });
  }, []);

  // Désélectionner un bloc
  const deselectBlock = useCallback((blockId: string) => {
    setSelectedBlocks(prev => {
      const newSet = new Set(prev);
      newSet.delete(blockId);
      return newSet;
    });
  }, []);

  // Désélectionner tous les blocs
  const clearSelection = useCallback(() => {
    setSelectedBlocks(new Set());
  }, []);

  // Sélectionner tous les blocs
  const selectAllBlocks = useCallback((blocks: Block[]) => {
    const allBlockIds = blocks.map(block => block.id);
    setSelectedBlocks(new Set(allBlockIds));
  }, []);

  // Toggle sélection globale (sélectionner tout si rien n'est sélectionné, sinon tout désélectionner)
  const toggleSelectAll = useCallback((blocks: Block[]) => {
    if (selectedBlocks.size === 0) {
      // Rien n'est sélectionné, tout sélectionner
      selectAllBlocks(blocks);
    } else {
      // Des blocs sont sélectionnés, tout désélectionner
      clearSelection();
    }
  }, [selectedBlocks.size, selectAllBlocks, clearSelection]);

  // Commencer la sélection par zone
  const startAreaSelection = useCallback((x: number, y: number, isAdditive = false) => {
    setIsSelecting(true);
    setSelectionStart({ x, y });
    setSelectionEnd({ x, y });
    
    if (!isAdditive) {
      clearSelection();
    }
  }, [clearSelection]);

  // Mettre à jour la sélection par zone
  const updateAreaSelection = useCallback((x: number, y: number) => {
    if (isSelecting) {
      setSelectionEnd({ x, y });
    }
  }, [isSelecting]);

  // Terminer la sélection par zone
  const endAreaSelection = useCallback(() => {
    setIsSelecting(false);
    setSelectionStart(null);
    setSelectionEnd(null);
  }, []);

  // Obtenir les blocs dans une zone de sélection
  const getBlocksInSelectionArea = useCallback((
    blocks: Block[],
    editorRect: DOMRect
  ): string[] => {
    if (!selectionStart || !selectionEnd || !isSelecting) return [];

    const selectedBlockIds: string[] = [];
    
    // Calculer les coordonnées de la zone de sélection
    const selectionTop = Math.min(selectionStart.y - editorRect.top, selectionEnd.y - editorRect.top);
    const selectionBottom = Math.max(selectionStart.y - editorRect.top, selectionEnd.y - editorRect.top);
    const selectionLeft = Math.min(selectionStart.x - editorRect.left, selectionEnd.x - editorRect.left);
    const selectionRight = Math.max(selectionStart.x - editorRect.left, selectionEnd.x - editorRect.left);
    
    // Ajouter une marge de tolérance
    const tolerance = 2;
    const adjustedSelectionTop = selectionTop - tolerance;
    const adjustedSelectionBottom = selectionBottom + tolerance;
    const adjustedSelectionLeft = selectionLeft - tolerance;
    const adjustedSelectionRight = selectionRight + tolerance;
    
    blocks.forEach(block => {
      const blockElement = document.querySelector(`[data-block-id="${block.id}"]`) as HTMLElement;
      if (blockElement) {
        const rect = blockElement.getBoundingClientRect();
        
        const blockTop = rect.top - editorRect.top;
        const blockBottom = rect.bottom - editorRect.top;
        const blockLeft = rect.left - editorRect.left;
        const blockRight = rect.right - editorRect.left;
        
        // Vérifier si le bloc intersecte avec la zone de sélection
        if (blockTop < adjustedSelectionBottom && blockBottom > adjustedSelectionTop &&
            blockLeft < adjustedSelectionRight && blockRight > adjustedSelectionLeft) {
          selectedBlockIds.push(block.id);
        }
      }
    });
    
    return selectedBlockIds;
  }, [selectionStart, selectionEnd, isSelecting]);

  // Vérifier si un bloc est sélectionné
  const isBlockSelected = useCallback((blockId: string) => {
    return selectedBlocks.has(blockId);
  }, [selectedBlocks]);

  // Obtenir les blocs sélectionnés
  const getSelectedBlocks = useCallback((blocks: Block[]): Block[] => {
    return blocks.filter(block => selectedBlocks.has(block.id));
  }, [selectedBlocks]);

  return {
    selectedBlocks,
    isSelecting,
    selectionStart,
    selectionEnd,
    selectionRef,
    selectBlock,
    selectRange,
    toggleSelection,
    deselectBlock,
    clearSelection,
    selectAllBlocks,
    toggleSelectAll,
    startAreaSelection,
    updateAreaSelection,
    endAreaSelection,
    getBlocksInSelectionArea,
    isBlockSelected,
    getSelectedBlocks
  };
};
